import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { config } from 'config';
import { type GetTasksParams, getTask, getTasksList } from '~/modules/tasks/api';
import { getOffset } from '~/query/helpers';

/**
 * Keys for tasks queries
 */
export const taskKeys = {
  all: ['tasks'] as const,
  single: (id: string) => [...taskKeys.all, 'single', id] as const,
  list: {
    base: () => [...taskKeys.all, 'list'] as const,
    board: (filters: Pick<GetTasksParams, 'projectId' | 'orgIdOrSlug'>) => [...taskKeys.list.base(), 'board', filters] as const,
    table: (filters: GetTasksParams) => [...taskKeys.list.base(), 'table', filters] as const,
  },
  create: () => [...taskKeys.all, 'create'] as const,
  update: () => [...taskKeys.all, 'update'] as const,
  delete: () => [...taskKeys.all, 'delete'] as const,
};

/**
 * Query Options to get list of tasks
 * @param params - GetTasksParams.
 * @returns Query options.
 */
export const tasksBoardQueryOptions = ({ projectId, orgIdOrSlug }: GetTasksParams) => {
  return queryOptions({
    queryKey: taskKeys.list.board({ projectId, orgIdOrSlug }),
    queryFn: async () =>
      await getTasksList({
        // default for board show accepted 2 weeks ago
        // TODO later we can perhaps create option in frontend for this
        acceptedCutOff: 14,
        orgIdOrSlug,
        projectId,
      }),
  });
};
/**
 * Query Options to get task by it's id and organization id
 * @param id - Task id.
 * @param orgIdOrSlug - Organization id or slug.
 * @returns Query options.
 */
export const taskQueryOptions = (id: string, orgIdOrSlug: string) =>
  queryOptions({
    queryKey: taskKeys.single(id),
    queryFn: () => getTask({ id, orgIdOrSlug }),
  });

/**
 * Infinite Query Options to list of tasks for table
 * @param params - GetTasksParams.
 * @returns Infinite query options.
 */
export const tasksTableQueryOptions = ({
  q,
  sort: initialSort,
  order: initialOrder,
  limit = config.requestLimits.tasksTable,
  projectId,
  status,
  orgIdOrSlug,
  // in table don't use acceptedCutOff
}: Omit<GetTasksParams, 'acceptedCutOff'>) => {
  const sort = initialSort || 'createdAt';
  const order = initialOrder || 'desc';

  const queryKey = taskKeys.list.table({ orgIdOrSlug, projectId, status, q, sort, order });

  return infiniteQueryOptions({
    queryKey,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam: page, signal }) => {
      const offset = getOffset(queryKey); // Calculate before fetching ensuring correct offset
      return await getTasksList({ page, q, sort, order, limit, offset, projectId, status, orgIdOrSlug }, signal);
    },
    getNextPageParam: (_lastPage, allPages) => allPages.length,
  });
};
